import Link from 'next/link'

import { Text } from '@wartek-id/text-v1'
import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Icon } from '@wartek-id/icon'

import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { useApiGetSchoolList } from 'api/kinerja/kepala-sekolah/school'
import { PENGELOLAAN_KINERJA_KEPALA_SKB_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import StateEmpty from 'app/KinerjaShared/components/StateEmpty'
import StateError from 'app/KinerjaShared/components/StateError'

import type { KinerjaKsStatus } from 'types/kinerja-ks'

const SCHOOL_BADGE: Record<
  KinerjaKsStatus,
  { text: string; variant: BadgeProps['variant'] }
> = {
  NOT_FOUND: { text: 'Belum dimulai', variant: 'neutral' },
  DRAFT: { text: 'Perencanaan', variant: 'neutral' },
  READY: { text: 'Pelaksanaan dan Penilaian', variant: 'critical' },
}

export function DaftarSekolah() {
  // -----------------------------------------
  // Get school list data
  // -----------------------------------------
  const {
    isLoading,
    data: schoolListData,
    isError,
  } = useApiGetSchoolList({
    enabled: true,
    useErrorBoundary: false,
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  // Only show SPNF schools
  const spnfList =
    schoolListData?.data?.filter(
      (school) =>
        school.role === 'KS_DEFINITIF_SPNF' || school.role === 'KS_PLT_SPNF'
    ) || []

  return (
    <div className="flex w-full min-h-screen">
      <div className="flex-shrink-0 min-h-full">
        <div className="w-64">{/* Sentinel element for sidebar spacing */}</div>
        <KinerjaSidebar activeMenu="kepala-skb-pegawai" sticky />
      </div>
      <main className="w-full p-6 space-y-6">
        <Text variant="heading-md" className="mb-9">
          Daftar Sekolah
        </Text>

        {isLoading ? (
          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-8">
            <Shimmer width="100%" height={124} />
            <Shimmer width="100%" height={124} />
            <Shimmer width="100%" height={124} />
            <Shimmer width="100%" height={124} />
            <Shimmer width="100%" height={124} />
          </div>
        ) : isError ? (
          <StateError />
        ) : spnfList.length > 0 ? (
          <ul className="grid grid-cols-2 2xl:grid-cols-3 gap-8">
            {spnfList.map((school) => {
              const role =
                school.role === 'KS_DEFINITIF_SPNF'
                  ? 'Kepala SKB Definitif'
                  : 'Plt. Kepala SKB Definitif'
              const statusBadge = SCHOOL_BADGE[school.state]

              return (
                <li
                  key={school.npsn}
                  className="border-subdued border-solid border rounded bg-white px-6 py-4 flex items-center gap-2"
                >
                  <div className="space-y-2 flex-1">
                    <Text
                      variant="heading-sm"
                      className="overflow-wrap-anywhere"
                    >
                      {school?.schoolName || '-'}
                    </Text>
                    <Text variant="body-sm" className="overflow-wrap-anywhere">
                      {role}
                    </Text>
                    {statusBadge && (
                      <Badge variant={statusBadge.variant}>
                        {statusBadge.text}
                      </Badge>
                    )}
                  </div>

                  <Link
                    href={`${PENGELOLAAN_KINERJA_KEPALA_SKB_PEGAWAI_PATH}/${school.npsn}`}
                    passHref
                  >
                    <Button
                      variant="ghost"
                      as="a"
                      type={undefined}
                      iconOnly={true}
                      className="!rounded-full"
                      color="white"
                    >
                      <Icon color="informational">chevron_right</Icon>
                      <span className="sr-only">
                        Buka {school.schoolName || ''}
                      </span>
                    </Button>
                  </Link>
                </li>
              )
            })}
          </ul>
        ) : (
          <StateEmpty title="Tidak ada data Sanggar Kegiatan Belajar untuk ditampilkan">
            Pastikan terdapat Sanggar Kegiatan Belajar di daerah Anda
          </StateEmpty>
        )}
      </main>
    </div>
  )
}
